import { useEffect, useState, useRef } from "react";
import { login, finishWebauthnLogin } from "../lib/auth";
import { ApiError, rateLimitMessage } from "../lib/api";
import { lastKickReason, clearLastKickReason } from "../lib/api";
import { kickCopy } from "../lib/session-kick-copy";
import { fetchLoginNfcRequired } from "../lib/punch";
import { scanTagUid } from "../lib/nfc";
import { nfcScanErrorMessage } from "../lib/nfc-login-copy";
import {
  executeWebAuthnAuthentication,
  mapWebAuthnError,
} from "../lib/webauthn";
import type { SessionUser } from "../lib/api";
import Button from "../components/Button";
import Card from "../components/Card";

// SPEC-70 §2.9 — the third step is entered on the SERVER 401 only (r3 #7):
// never on `title`, which is a UI label. An office-typed dispatcher
// credential must still get a working NFC_SCAN step.
type Step = "CREDENTIALS" | "WEBAUTHN" | "NFC_SCAN";

/**
 * SPEC-70 §2.9 — can THIS device scan a tag at all? True inside the iOS
 * shell (the eagleNFC bridge — the same fingerprint lib/nfc.ts and
 * PasskeyEnrollmentCard probe) or on Android Chrome (NDEFReader). Exported
 * for tests.
 */
export function deviceCanScanNfc(): boolean {
  if (typeof window === "undefined") return false;
  const w = window as unknown as {
    webkit?: { messageHandlers?: { eagleNFC?: unknown } };
    NDEFReader?: unknown;
  };
  if (w.webkit?.messageHandlers?.eagleNFC) return true;
  return typeof w.NDEFReader !== "undefined";
}

export default function Login({
  title = "Dispatcher Login",
  onLoggedIn,
}: {
  title?: string;
  // SPEC-76 §2.1 — the second arg carries the login-scan tag UID so the
  // dispatcher surface can auto-clock-IN from the persistent shell. Optional:
  // the other session-establishing sites (WebAuthn success, bound-device
  // bypass submit, password-only fallback) performed NO scan and pass none.
  onLoggedIn: (user: SessionUser, loginScanUid?: string) => void;
}) {
  const [step, setStep] = useState<Step>("CREDENTIALS");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // SPEC-70 §2.9 — proactive state. `nfcRequired` caches the config probe
  // (null = not loaded yet); `nfcUnsupported` is the capability probe. Both
  // are AFFORDANCE state: the NFC_SCAN step is entered on the server 401
  // alone (r3 #7), so a stale or failed probe can never block a dispatcher
  // whose device CAN scan.
  const [nfcRequired, setNfcRequired] = useState<boolean | null>(null);
  const [nfcUnsupported, setNfcUnsupported] = useState(false);

  // SPEC-71 §3C — durable kick backstop: read once (lazy initializer) and
  // clear the module var on mount so the line is displayed exactly once.
  const [kickReason] = useState<string | null>(() => lastKickReason());
  useEffect(() => {
    clearLastKickReason();
  }, []);

  // SPEC-70 §2.9 — probe on mount: is the login gate on, and can this device
  // scan? Rendering-only. The probe FAILS CLOSED (lib/punch.ts — it resolves
  // true on transport failure), so an unreachable config shows the constraint
  // copy rather than risking a surprise 401 loop on a scan-less desktop.
  useEffect(() => {
    let cancelled = false;
    void fetchLoginNfcRequired()
      .then((required) => {
        if (!cancelled) {
          setNfcRequired(required);
          setNfcUnsupported(required && !deviceCanScanNfc());
        }
      })
      .catch(() => {
        // The probe is fail-closed and never rejects — defensive only.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // WebAuthn ceremony tracking & abort refs (SPEC-43 Fix 1 & 2)
  const ceremonyRef = useRef(false);
  const abortedRef = useRef(false);

  // WebAuthn 2nd step state
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [authOptions, setAuthOptions] = useState<Record<string, unknown> | null>(null);

  const startWebauthnPrompt = async (
    token: string,
    options: Record<string, unknown>,
  ) => {
    if (ceremonyRef.current) return;
    ceremonyRef.current = true;
    abortedRef.current = false;
    setSubmitting(true);
    setError(null);
    try {
      const assertion = await executeWebAuthnAuthentication(options as never);
      if (abortedRef.current) return;
      const user = await finishWebauthnLogin(token, assertion as never);
      if (abortedRef.current) return;
      onLoggedIn(user);
    } catch (err) {
      if (abortedRef.current) return;
      // SPEC-66a: a 429 rate_limited is not a WebAuthn ceremony failure or a
      // replay — surface the server's wait time. Checked FIRST, before the
      // replay check and mapWebAuthnError (which is blind to rate_limited).
      // The early return still runs the `finally` below (ceremony/submitting
      // reset), same as the abortedRef early return above.
      const rl = rateLimitMessage(err);
      if (rl) {
        setError(rl);
        return;
      }
      const apiErr = err as ApiError;
      if (
        apiErr &&
        typeof apiErr === "object" &&
        apiErr.code === "unauthorized" &&
        typeof apiErr.message === "string" &&
        /replay/i.test(apiErr.message)
      ) {
        setError("This sign-in was already completed in another tab. Please sign in again.");
      } else {
        setError(mapWebAuthnError(err, { context: "login" }));
      }
    } finally {
      ceremonyRef.current = false;
      setSubmitting(false);
    }
  };

  // SPEC-70 §2.9 — which fallback class was in effect when the server 401'd.
  // submit's NFC branch records false; submitFallback's records true (the
  // resubmit MUST carry fallback:true or the server mints a challenge instead
  // of a session). Reset by the NFC_SCAN back button. A ref, not state: it is
  // consumed by the resubmit, never rendered.
  const nfcFallbackRef = useRef(false);

  /**
   * SPEC-70 §2.9 — the NFC_SCAN step's resubmit: state-held credentials +
   * the scanned tag + the captured fallback class (submitFallback's
   * resubmit-from-state pattern, extended with tagUid). A requiresWebAuthn
   * response (gate passed, policy demands the key) transitions to WEBAUTHN
   * and starts the ceremony — the gate sits before challenge issuance, so
   * this is the normal credential-bearing-dispatcher continuation.
   */
  const resubmitWithTag = async (tagUid: string, fallback: boolean) => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await login(username.trim(), password, {
        tagUid,
        ...(fallback ? { fallback: true } : {}),
      });
      if (res.requiresWebAuthn) {
        setStep("WEBAUTHN");
        setChallengeToken(res.challengeToken);
        setAuthOptions(res.options);
        void startWebauthnPrompt(res.challengeToken, res.options);
      } else {
        setSubmitting(false);
        // SPEC-76 §2.1 — retain the login-scan UID for the dispatcher
        // single-scan auto-punch. The ONLY onLoggedIn site holding a scanned
        // tagUid: the WebAuthn success, the bound-device bypass submit, and
        // the password-only fallback performed no scan and pass none.
        onLoggedIn(res.user, tagUid);
      }
    } catch (resubmitErr) {
      setSubmitting(false);
      // SPEC-66a 429-first holds on the resubmit too (the nfcfail bucket or
      // the route IP bucket can 429 here).
      const rl = rateLimitMessage(resubmitErr);
      setError(
        rl ??
          (resubmitErr instanceof Error
            ? resubmitErr.message
            : String(resubmitErr)),
      );
    }
  };

  /** The NFC_SCAN step's Scan button: scan → resubmit with the UID. */
  const scanAndResubmit = async () => {
    const result = await scanTagUid();
    if ("uid" in result) {
      await resubmitWithTag(result.uid, nfcFallbackRef.current);
      return;
    }
    setError(nfcScanErrorMessage(result.error));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await login(username.trim(), password);
      if (res.requiresWebAuthn) {
        setStep("WEBAUTHN");
        setChallengeToken(res.challengeToken);
        setAuthOptions(res.options);
        // Automatically trigger ceremony on transition
        void startWebauthnPrompt(res.challengeToken, res.options);
      } else {
        setSubmitting(false);
        onLoggedIn(res.user);
      }
    } catch (err) {
      setSubmitting(false);
      // SPEC-75 — a 3-strike-locked account answers 401 password_locked on
      // EVERY password attempt (even the correct one) until the office
      // resets it. Discriminated before the rate-limit/generic mapping in
      // both catches, mirroring the nfc_login_required discrimination.
      if (err instanceof ApiError && err.code === "password_locked") {
        setError("Account locked — contact the office to reset your password.");
        return;
      }
      // SPEC-70 §2.9 — entered on the server 401 ONLY, never on `title`
      // (r3 #7): an office-typed dispatcher credential still gets a working
      // NFC_SCAN step. Discriminated FIRST — every other failure keeps the
      // SPEC-66a sequence below verbatim.
      if (err instanceof ApiError && err.code === "nfc_login_required") {
        nfcFallbackRef.current = false;
        setStep("NFC_SCAN");
        setError(null);
        return;
      }
      // SPEC-66a: 429 rate_limited → the server's wait time; every other
      // error keeps the pre-existing default message.
      const rl = rateLimitMessage(err);
      setError(rl ?? (err instanceof Error ? err.message : String(err)));
    }
  };

  const handleBackToPassword = () => {
    abortedRef.current = true;
    ceremonyRef.current = false;
    setSubmitting(false);
    setStep("CREDENTIALS");
    setChallengeToken(null);
    setAuthOptions(null);
    setError(null);
  };

  // SPEC-70 §2.9 — leave the NFC_SCAN step without scanning.
  const handleBackFromNfcScan = () => {
    nfcFallbackRef.current = false;
    setStep("CREDENTIALS");
    setError(null);
  };

  // SPEC-68 §4.13 — password-only fallback (dispatcher surface only). The
  // pending ceremony must not be allowed to also complete: startWebauthnPrompt
  // fires automatically on entering this step, and its success path calls
  // onLoggedIn guarded only by abortedRef. Setting the ref BEFORE login() means
  // a slow key-touch resolving after the fallback session landed is discarded
  // instead of calling onLoggedIn a second time with a different session.
  // The ref is deliberately never cleared here — handleBackToPassword and
  // startWebauthnPrompt are the only writers that reset it.
  const submitFallback = async () => {
    abortedRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const res = await login(username.trim(), password, { fallback: true });
      if (res.requiresWebAuthn) {
        // Defensive only: with fallback:true the server must bypass the
        // ceremony (SPEC-68 §4.5c). Rendering the step again would loop the
        // user, so surface a plain error instead.
        setError("This account still requires a security key.");
        setSubmitting(false);
        return;
      }
      onLoggedIn(res.user);
    } catch (err) {
      setSubmitting(false);
      // SPEC-75 — same password_locked discrimination as submit's catch
      // (the fallback resubmit can hit the locked 401 too).
      if (err instanceof ApiError && err.code === "password_locked") {
        setError("Account locked — contact the office to reset your password.");
        return;
      }
      // SPEC-70 §2.9 — same server-401-only NFC_SCAN entry as submit's catch,

      // but the resubmit must preserve the fallback class (a dispatcher who
      // chose the shorter session stays in it after scanning).
      if (err instanceof ApiError && err.code === "nfc_login_required") {
        nfcFallbackRef.current = true;
        setStep("NFC_SCAN");
        setError(null);
        return;
      }
      // Same 429-first contract as every other catch site (SPEC-66a).
      const rl = rateLimitMessage(err);
      setError(rl ?? (err instanceof Error ? err.message : String(err)));
    }
  };

  return (
    <main className="login-screen">
      <Card className="login-card">
        <img
          className="login-brand-logo"
          src="/eagle-dispatch-logo.jpg"
          alt="Eagle Dispatch"
        />
        <h1 className="login-title">{title}</h1>

        {step === "CREDENTIALS" ? (
          <>
            {/* SPEC-70 §2.9 — proactive desktop constraint, rendered BEFORE
                the form. Affordance only: the reactive 401 branch still owns
                correctness (r3 #7). */}
            {nfcRequired && nfcUnsupported && (
              <div className="banner banner-danger" style={{ marginBottom: "16px" }}>
                {nfcScanErrorMessage("unsupported")}
              </div>
            )}
            <form className="login-form" onSubmit={submit}>
              <label className="field">
                <span className="field-label">Username</span>
                <input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  autoFocus
                />
              </label>
              <label className="field">
                <span className="field-label">Password</span>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                />
              </label>
              {error && <p className="form-error err">{error}</p>}
              <Button
                type="submit"
                variant="primary"
                className="login-button"
                disabled={submitting || !username.trim() || !password}
              >
                {submitting ? "Signing in…" : "Sign In"}
              </Button>
            </form>
            {kickReason && (
              <p className="muted kick-reason" style={{ fontSize: "13px" }}>
                {kickCopy(kickReason)}
              </p>
            )}
          </>
        ) : step === "NFC_SCAN" ? (
          <div className="webauthn-step-container" style={{ textAlign: "center", padding: "16px 0" }}>
            <div style={{ fontSize: "40px", marginBottom: "12px" }}>📱</div>
            <h2 style={{ fontSize: "18px", margin: "0 0 8px 0" }}>
              Scan the breakroom NFC tag to sign in
            </h2>
            <p className="muted" style={{ fontSize: "14px", margin: "0 0 16px 0" }}>
              Hold your phone to the tag until it beeps.
            </p>

            {error && (
              <div className="banner banner-danger" style={{ marginBottom: "16px", textAlign: "left" }}>
                {error}
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <Button
                type="button"
                variant="primary"
                className="login-button"
                disabled={submitting}
                onClick={() => void scanAndResubmit()}
              >
                {submitting ? "Signing in…" : "Scan Tag"}
              </Button>

              <Button
                type="button"
                variant="ghost"
                onClick={handleBackFromNfcScan}
              >
                ← Back to Password
              </Button>
            </div>
          </div>
        ) : (
          <div className="webauthn-step-container" style={{ textAlign: "center", padding: "16px 0" }}>
            <div style={{ fontSize: "40px", marginBottom: "12px" }}>🔑</div>
            <h2 style={{ fontSize: "18px", margin: "0 0 8px 0" }}>Touch your security key or use your device's passkey</h2>
            <p className="muted" style={{ fontSize: "14px", margin: "0 0 16px 0" }}>
              On a YubiKey: insert it and tap the golden contact.
            </p>

            {error && (
              <div className="banner banner-danger" style={{ marginBottom: "16px", textAlign: "left" }}>
                {error}
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <Button
                type="button"
                variant="primary"
                className="login-button"
                disabled={submitting}
                onClick={() => {
                  if (challengeToken && authOptions) {
                    void startWebauthnPrompt(challengeToken, authOptions);
                  }
                }}
              >
                {submitting ? "Waiting for key touch…" : "Retry Key Prompt"}
              </Button>

              {/* SPEC-68 §4.13 item 1 — dispatcher-only password fallback
                  (§4.5c: office has no fallback; the server rejects it with
                  403 fallback_not_permitted). This component serves BOTH
                  login surfaces, discriminated by the title prop. */}
              {title !== "Office Login" && (
                <Button
                  type="button"
                  variant="ghost"
                  disabled={submitting}
                  onClick={() => void submitFallback()}
                >
                  Use password only (shorter session)
                </Button>
              )}

              <Button
                type="button"
                variant="ghost"
                onClick={handleBackToPassword}
              >
                ← Back to Password
              </Button>
            </div>
          </div>
        )}

      </Card>
    </main>
  );
}
